import {useCallback} from 'react';

import {getLineupByName} from 'assets/lineups';
import {canvasToJson} from 'canvas/canvas-helper';
import {createPlayer as createPlayerShirt} from 'canvas/Drawer/Player';
import {createPlayerCircle} from 'canvas/Drawer/PlayerCircle';
import {useCanvasStore} from 'canvas/zustand';
import {useAnalytics} from 'hooks';
import {memberInfo} from 'module/client/team/member/useInfo';
import {groupByPosition} from 'module/client/team/member/useMemberApi';
import {useApiQuery} from 'module/client/useClientApi';
import {usePrefsStore} from 'store';

import {positions} from './useTeamApi';

export const useDrawTeamLineup = () => {
    const canvas = useCanvasStore(state => state.canvas);
    const setDirty = useCanvasStore(state => state.setDirty);
    const pushCanvas = useCanvasStore(state => state.pushCanvas);
    const color = usePrefsStore((state) => state.color);
    const {data: clients} = useApiQuery();
    const sendAnalytics = useAnalytics();

    return useCallback((players, team, lineupName, direction, type, size, defaultType) => {
        console.log('drawTeamLineup', team?.name, players, team, lineupName, direction, type, size);
        setDirty(true);
        sendAnalytics('action', 'lineup', {team: team?.name, lineup: lineupName});

        const byPosition = groupByPosition(players);

        const lineup = getLineupByName(direction, lineupName);
        if (!lineup) {
            return;
        }

        if (!canvas) {
            console.error('Canvas not found');
            return; 
        }

        canvas.isLoading = true; // avoid pushCanvas for each player

        // Get default colors from client if not set in team prefs
        const getDefaultColors = () => {
            const teamClient = clients?.find(client => client.id === team.client_id);
            if (teamClient?.colors?.value1 && teamClient?.colors?.value2) {
                return {
                    value1: teamClient.colors.value1,
                    value2: teamClient.colors.value2,
                    textColor: teamClient.colors.value2
                };
            }

            return {
                value1: !defaultType || defaultType === 'home' ? 'black' : 'white',
                value2:  !defaultType || defaultType === 'home' ? 'white' : 'black',
                textColor:  !defaultType || defaultType === 'home' ? 'white' : 'black',
            };
        };

        const defaultColors = getDefaultColors();
                
        // fallback to selected color if not set in team prefs
        const colors = {
            value1: team.prefs.player.color1 || defaultColors.value1, 
            value2: team.prefs.player.color2 || defaultColors.value2, 
            textColor: team.prefs.player.color2 || defaultColors.textColor, 
        };
        const targetPlayerType = type || team?.prefs?.player?.type || 'player';
        // TODO remove all players of team on team change and replace with new players
        positions.forEach(pos => {
            const coordinates = lineup.positions?.[pos];
            if (!coordinates) {
                return;
            }

            const existingLine = findPlayersByPosition(canvas, team.id, pos);
            // TODO fix for initial placements existingline is empty
            console.log(pos, existingLine.length, coordinates.length);

            // remove surplus players
            const numRemove = existingLine.length - coordinates.length;
            for (let i = 0; i < numRemove; i++) {
                canvas.remove(existingLine.pop());
            }

            // remove players, which are already on the canvas
            const availablePlayers = byPosition[pos].filter(p => !existingLine.find(e => e.custom.playerId === p.id));

            // add missing players
            for (let index = existingLine.length; index < coordinates.length; index++) {
                let player = availablePlayers.shift();

                // no more players available
                if (!player) {
                    break;
                }

                const c = getCoordinate(canvas, coordinates, index);
      
                // const size = mapPlayerSize(prefs.player.size);
                const image = targetPlayerType === 'playerCircleImage' && player.user?.image ? player.user?.image : null;
                createPlayer(canvas, player, targetPlayerType, colors, size, image, c.left, c.top, {
                    team: team.id,
                    playerId: player.id,
                    position: pos
                });
            }

            // Update existing players with new colors and type
            const line = findPlayersByPosition(canvas, team.id, pos);
            
            line.forEach((player, index) => {
                const currentPlayerType = player.custom?.subType;
                // If player type has changed, remove old player and create new one
                const playerData = byPosition[pos].find(p => p.id === player.custom?.playerId);
                // Find the corresponding player data
                if (playerData) {
                    const image = targetPlayerType === 'playerCircleImage' && playerData.user?.image ? playerData.user?.image : null;
                    // catch case where target is playerImage but no image is available
                    if ((image && currentPlayerType !== targetPlayerType && targetPlayerType === 'playerCircleImage') || (!image && currentPlayerType !== targetPlayerType && targetPlayerType !== 'playerCircleImage')) {
                        console.log('updatePlayerType', targetPlayerType, currentPlayerType);
                        canvas.remove(player);
                        const c = getCoordinate(canvas, coordinates, index);
                        createPlayer(canvas, playerData, targetPlayerType, colors, size, image, c.left, c.top, {
                            team: team.id,
                            playerId: playerData.id,
                            position: pos
                        });
                    } else {
                        // Update player colors for existing type
                        if (player.custom?.subType === 'player') {
                            const objects = player.getObjects();
                            // For player shirts (SVG groups)
                            const svgGroup = objects[0];
                            if (svgGroup && typeof svgGroup.getObjects === 'function') {
                                const svgObjects = svgGroup.getObjects();
                                svgObjects[0]?.set('fill', colors.value1);
                                svgObjects[1]?.set('fill', colors.value2);
                            }

                            // Update number text color
                            if (objects[1]) {
                                objects[1].set('fill', colors.textColor);
                            }
                        } else if (player.custom?.subType === 'playerCircle') {
                            const objects = player.getObjects();
                            // For player circles
                            if (objects[0]) {
                                objects[0].set('fill', colors.value1);
                                objects[0].set('stroke', colors.value2);
                            }

                            // Update number text color
                            if (objects[1]) {
                                objects[1].set('fill', colors.textColor);
                            }
                        }

                        console.log('ANIMATE PLAYER');
                        // Animate to new position
                        player.animate(getCoordinate(canvas, coordinates, index), {
                            duration: 800,
                            onChange: canvas.renderAll.bind(canvas),
                        });
                    }
                } else {
                    console.log('No player data for player:', player.id, 'on position:', pos);
                }
            });

        });

        pushCanvas(canvasToJson(canvas));
        canvas.isLoading = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canvas, pushCanvas, sendAnalytics, setDirty, color, clients]);
};

export const createPlayer = (canvas, player, type, colors, size, image, left, top, customData = {}) => {
    const {name} = memberInfo(player);

    const onComplete = (cvs, playerGroup) => {
        // Simply add the player to canvas
        // The name is now created as part of the initial group
        cvs.add(playerGroup);
    };

    switch (type) {
    case 'playerCircle':
        // console.log('create',type, player, type, colors, size, image, left, top, customData);
        createPlayerCircle(canvas, colors, size, left, top, player.number, null, customData, onComplete, name);
        break;
    case 'playerCircleImage':
        // console.log('create:',type, player, type, colors, size, image, left, top, customData);
        createPlayerCircle(canvas, colors, size, left, top, player.number, image, customData, onComplete, name);
        break;
    case 'player':
        // console.log('create:', type, player, type, colors, size, image, left, top, customData);
        createPlayerShirt(canvas, 'player', colors, size, left, top, player.number, customData, onComplete, name);
            
        break;
    
    default:
        break;
    }

};

const getCoordinate = (canvas, coordinates, index) => {
    const left = Math.round(coordinates[index].x);
    const top = Math.round(coordinates[index].y);

    return {left, top};
};

/**
 * Finds players on a canvas by their position.
 *
 * @param {fabric.Canvas} canvas - The canvas object.
 * @param {string} teamId - The team id for holding the players together
 * @param {string} position - The position (goal, defense, ...) of the players to find.
 * @returns {fabric.Object[]} - An array of fabric objects representing the found players.
 */
const findPlayersByPosition = (canvas, teamId, position) => {
    if (!canvas) {
        return [];
    }

    return canvas.getObjects().filter(e => e.custom?.team === teamId && e.custom?.position === position);
};